import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { ButtonRow, StyledButton, TitleText, TitleWrapper } from "../../../shared/styled/TitleBar";
import AddIcon from "../../../shared/svg/AddIcon";
import DownloadIcon from "../../../shared/svg/DownloadIcon";
import ExportIcon from "../../../shared/svg/ExportIcon";
import ImportIcon from "../../../shared/svg/ImportIcon";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import apiMethods from "../../../utils/api";
import { ColumnMapping, downloadExcel } from "../../../utils/exportUtils";
import { error, warning } from "../../../utils/toast";
import { ExportMaterialModal } from "../ExportMaterialModal";
import { ImportMaterialModal } from "../ImportMaterialModal";
import ExportMaterialQuantitySummaryModal from "./ExportMaterialQuantitySummaryModal";
import { buildMaterialReportParams, MATERIAL_QUANTITY_SUMMARY_EXPORT_URL } from "./reportExport";
import MaterialStockCardModal from "./MaterialStockCardModal";
import { canCreateMaterialStockCard } from "./materialStockCardPermissions";

const Title = () => {
    const navigate = useNavigate();
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);   
    const [isExportQuantitySummaryModalOpen, setIsExportQuantitySummaryModalOpen] = useState(false);
    const [isStockCardModalOpen, setIsStockCardModalOpen] = useState(false);
    const [materials, setMaterials] = useState<any[]>([]);
    const [isExportingReports, setIsExportingReports] = useState(false);
    // Remove the API hook since we need to call the API directly with parameters
    // const [{ exportQuantitySummary, isFetching }, fetchExportQuantitySummary] = api.get(`/materials/quantity-tracking/?`);
    
    const {
        user: { role },
    } = useContext(AuthenticationContext);

    const canCreate = [
        ROLE.MANAGER,
        ROLE.LOGISTIC_STAFF,
        ROLE.STORAGE_KEEPER,
        ROLE.FIRST_STORAGE_KEEPER,
        ROLE.SECOND_STORAGE_KEEPER,
        ROLE.PURCHASING_STAFF,
    ].includes(role);
    const canCreateStockCard = canCreateMaterialStockCard(role);

    const onClickAddButton = () => {
        navigate("/materials/create");
    };

    const onClickImportButton = async () => {
        try {
            const response: any = await apiMethods.get("/materials/");
            setMaterials(response?.results || []);
            setIsImportModalOpen(true);
        } catch (err) {
            error("Không thể tải danh sách vật tư", err);
        }
    };

    const onClickExportButton = async () => {
        try {
            const response: any = await apiMethods.get("/materials/");
            setMaterials(response?.results || []);
            setIsExportModalOpen(true);
        } catch (err) {
            error("Không thể tải danh sách vật tư", err);
        }
    };

    const downloadQuantitySummaryReport = async (thisDateRange: [string | null, string | null], params: any) => {
        const response: any = await apiMethods.get(
            MATERIAL_QUANTITY_SUMMARY_EXPORT_URL,
            buildMaterialReportParams(thisDateRange, params),
        );
        const data = response?.data || response;

        if (data.length === 0) {
            warning("Báo cáo nhập xuất tồn", "Không có dữ liệu thỏa yêu cầu");
            return;
        }

        if (data && Array.isArray(data)) {
            const columnMapping: ColumnMapping = {
                material_code: "Mã vật tư",
                name: "Tên Vật Tư",
                storage: "Kho",
                start_quantity: "Số lượng tồn đầu",
                total_imported: "Số lượng nhập",
                total_exported: "Số lượng xuất",
                end_quantity: "Số lượng tồn cuối",
            };
            downloadExcel(
                data,
                columnMapping,
                "Thống kê vật tư",
                `thong_ke_vat_tu_${thisDateRange[0]}_${thisDateRange[1]}`
            );
        } else {
            throw new Error("Dữ liệu không hợp lệ");
        }
    };

    const downloadExportedMaterialsReport = async (thisDateRange: [string | null, string | null], params: any) => {
        const resp: any = await apiMethods.get("/materials/reports/exports/", {
            start_date: thisDateRange[0],
            end_date: thisDateRange[1],
            ...params
        });
        const rows = resp?.data || resp;

        if (rows.length === 0) {
            warning("Báo cáo xuất kho", "Không có dữ liệu thỏa yêu cầu");
            return;
        }

        const columnMapping: ColumnMapping = {
            export_date: "Ngày xuất",
            export_code: "Mã phiếu",
            material_name: "Tên vật tư",
            material_code: "Mã vật tư",
            exporter_name: "Người xuất kho",
            measurement_unit: "Đơn vị tính",
            quantity: "Số lượng",
            export_method: "Phương thức xuất",
            export_note: "Ghi chú của phiếu",
        };
        downloadExcel(
            rows,
            columnMapping,
            "Báo cáo chi tiết xuất",
            `bao_cao_vat_tu_da_xuat_${thisDateRange[0]}_${thisDateRange[1]}`
        );
    };

    const downloadImportedMaterialsReport = async (thisDateRange: [string | null, string | null], params: any) => {
        const resp: any = await apiMethods.get("/materials/reports/imports/", {
            start_date: thisDateRange[0],
            end_date: thisDateRange[1],
            ...params
        });
        let rows = resp?.data || resp;

        if (rows.length === 0) {
            warning("Báo cáo nhập kho", "Không có dữ liệu thỏa yêu cầu");
            return;
        }

        // Filter out MaterialOrder imports (input_method: "MaterialOrder")
        rows = rows.filter((row: any) => {
            const inputMethod = String(row.input_method || '').trim();
            // Exclude only MaterialOrder imports
            return inputMethod !== "MaterialOrder";
        });

        const columnMapping: ColumnMapping = {
            import_date: "Ngày nhập",
            import_code: "Mã phiếu",
            material_name: "Tên vật tư",
            material_code: "Mã vật tư",
            provider_code: "Mã NCC",
            deliverer: "Người giao hàng",
            measurement_unit: "Đơn vị tính",
            quantity: "Số lượng",
            due_date: "Ngày đến hạn",
            input_method: "Phương thức nhập",
            import_note: "Ghi chú của phiếu",
        };
        downloadExcel(
            rows,
            columnMapping,
            "Báo cáo chi tiết nhập",
            `bao_cao_vat_tu_da_nhap_${thisDateRange[0]}_${thisDateRange[1]}`
        );
    };

    const handleExportReports = async (selectedTypes: string[], thisDateRange: [string | null, string | null]) => {
        if (!thisDateRange[0] || !thisDateRange[1]) {
            error("Vui lòng chọn khoảng thời gian hợp lệ");
            return;
        }
        setIsExportingReports(true);
        try {
            const tasks: Promise<void>[] = [] as any;

            // I want to get all the current params from the url
            const urlParams = new URLSearchParams(window.location.search);
            const params = Object.fromEntries(urlParams.entries());

            
            if (selectedTypes.includes("Báo cáo nhập xuất tồn")) {
                tasks.push(
                    downloadQuantitySummaryReport(thisDateRange, params)
                );
            }

            if (selectedTypes.includes("Báo cáo chi tiết xuất")) {
                tasks.push(
                    downloadExportedMaterialsReport(thisDateRange, params)
                );
            }

            if (selectedTypes.includes("Báo cáo chi tiết nhập")) {
                tasks.push(
                    downloadImportedMaterialsReport(thisDateRange, params)
                );
            }


            await Promise.all(tasks);
        } catch (e) {
            error("Lỗi xuất file", e);
        } finally {
            setIsExportingReports(false);
        }
    };

    return (
        <>
            {isImportModalOpen && (
                <ImportMaterialModal
                    open={isImportModalOpen}
                    handleClose={() => setIsImportModalOpen(false)}
                />
            )}
            {isExportModalOpen && (
                <ExportMaterialModal
                    open={isExportModalOpen}
                    handleClose={() => setIsExportModalOpen(false)}
                    materials={materials}
                />
            )}
            {isExportQuantitySummaryModalOpen && (
                <ExportMaterialQuantitySummaryModal
                    open={isExportQuantitySummaryModalOpen}
                    handleClose={() => setIsExportQuantitySummaryModalOpen(false)}
                    handleConfirm={handleExportReports}
                    isLoading={isExportingReports}
                />
            )}
            {isStockCardModalOpen && (
                <MaterialStockCardModal
                    open={isStockCardModalOpen}
                    onClose={() => setIsStockCardModalOpen(false)}
                />
            )}
            <TitleWrapper style={{ flexWrap: "wrap", gap: "8px" }}>
                <TitleText>Danh sách vật tư</TitleText>
                <ButtonRow style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {canCreateStockCard && (
                        <StyledButton
                            variant="outlined"
                            onClick={() => setIsStockCardModalOpen(true)}
                            data-testid="create-material-stock-card"
                        >
                            <PictureAsPdfOutlinedIcon fontSize="small" />
                            Tạo thẻ kho
                        </StyledButton>
                    )}
                    <StyledButton variant="outlined" onClick={() => setIsExportQuantitySummaryModalOpen(true)}>
                        <DownloadIcon />
                        Tải file báo cáo
                    </StyledButton>

                    {canCreate && (
                        <>
                            <StyledButton variant="outlined" onClick={onClickImportButton}>
                                <ImportIcon />
                                Nhập kho
                            </StyledButton>
                            <StyledButton variant="outlined" onClick={onClickExportButton}>
                                <ExportIcon />
                                Xuất kho
                            </StyledButton>
                            <StyledButton variant="contained" onClick={onClickAddButton}>
                                <AddIcon />
                                Tạo mới
                            </StyledButton>
                        </>
                    )}
                </ButtonRow>
            </TitleWrapper>
        </>
    );
};

export default Title;
